import { useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    InputAdornment,
    IconButton
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import apiClient from '../connections/api'

const ChangePasswordDialog = ({ open, user, passProvisory, originalPassword, onPasswordChanged, onCancel }) => {
    const [currentPassword, setCurrentPassword] = useState(originalPassword || '')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const validatePassword = (pass) => {
        if (!pass || pass.length < 8) return 'La contraseña debe tener al menos 8 caracteres.'

        const uppercaseCount = (pass.match(/[A-Z]/g) || []).length
        const numberCount = (pass.match(/[0-9]/g) || []).length
        // Un símbolo es cualquier cosa que no sea letra o número
        const symbolRegex = /[^A-Za-z0-9]/
        const hasSymbol = symbolRegex.test(pass)

        if (uppercaseCount < 2) return 'Debe tener al menos 2 letras mayúsculas.'
        if (numberCount < 2) return 'Debe tener al menos 2 números.'
        if (!hasSymbol) return 'Debe tener al menos 1 símbolo (ej: #, @, !).'

        return null
    }

    const handleConfirm = async () => {
        setError('')

        if (!currentPassword) {
            setError('Debe ingresar su contraseña actual.')
            return
        }

        if (newPassword !== confirmPassword) {
            setError('Las nuevas contraseñas no coinciden.')
            return
        }

        const validationError = validatePassword(newPassword)
        if (validationError) {
            setError(validationError)
            return
        }

        setLoading(true)
        try {
            // Usamos el token provisorio para esta llamada
            const response = await apiClient.post('/auth/changePassword', {
                oldPassword: currentPassword,
                newPassword: newPassword
            }, {
                headers: {
                    Authorization: `Bearer ${passProvisory}`
                }
            })

            if (response.status === 200) {
                onPasswordChanged(newPassword)
            }
        } catch (err) {
            console.error('Error changing password:', err)
            setError(err.response?.data?.message || 'Error al cambiar la contraseña. Intente nuevamente.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>Actualización de Seguridad</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Su contraseña actual no cumple con los nuevos estándares de seguridad. Por favor, actualícela para continuar.
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        Requisitos: Mínimo 8 caracteres, 2 mayúsculas, 2 números y 1 símbolo.
                    </Alert>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <TextField
                        margin="dense"
                        label="Contraseña Actual"
                        type={showCurrentPassword ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                    >
                                        <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} size="xs" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        margin="dense"
                        label="Nueva Contraseña"
                        type={showNewPassword ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} size="xs" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        margin="dense"
                        label="Confirmar Nueva Contraseña"
                        type={showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} size="xs" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit" disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    variant="contained"
                    disabled={loading || !newPassword || !confirmPassword}
                >
                    Confirmar y Entrar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ChangePasswordDialog
